import { useEffect, useMemo, useState } from "react";
import type { DocumentValue } from "../types";
import { getDocumentItemId, getExplicitDocumentDescription } from "../utils/document-value";
import { useExternalDataStore } from "../../state/external-data-store";

export type { DocumentValue } from "../types";

type DocumentBrowserModalProps = {
  isOpen: boolean;
  instanceId: string;
  onClose: () => void;
  onPick: (item: DocumentValue) => void;
};

function toDocumentValue(doc: DocumentValue): DocumentValue {
  const itemId = getDocumentItemId(doc);
  const itemName = getExplicitDocumentDescription(doc);

  return {
    ...doc,
    id: itemId ?? doc.id,
    item_id: itemId,
    item_name: itemName || doc.item_name,
    description: itemName || doc.description,
  };
}

export function DocumentBrowserModal(props: DocumentBrowserModalProps) {
  const { isOpen, instanceId, onClose, onPick } = props;
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingDescription, setPendingDescription] = useState("");
  const [pendingCategoryId, setPendingCategoryId] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState("");

  const documentsBrowserState = useExternalDataStore(
    (state) => state.documentsBrowser[instanceId]
  );
  const setDocumentsBrowserScope = useExternalDataStore(
    (state) => state.setDocumentsBrowserScope
  );
  const listDocumentsAction = useExternalDataStore((state) => state.listDocuments);
  const listCategoriesAction = useExternalDataStore(
    (state) => state.listDocumentCategories
  );
  const uploadDocumentAction = useExternalDataStore((state) => state.uploadDocument);

  const documentScope = documentsBrowserState?.documentScope ?? "current";
  const categoryId = documentsBrowserState?.categoryId ?? 0;
  const sharedScopeAvailable = documentsBrowserState?.sharedScopeAvailable ?? null;
  const categories = documentsBrowserState?.categories ?? [];
  const items = documentsBrowserState?.items ?? [];
  const isLoading = documentsBrowserState?.isLoading ?? false;
  const listError = documentsBrowserState?.listError ?? null;
  const isUploading = documentsBrowserState?.isUploading ?? false;
  const uploadError = documentsBrowserState?.uploadError ?? null;
  const isLoadingCategories = documentsBrowserState?.isLoadingCategories ?? false;
  const categoriesError = documentsBrowserState?.categoriesError ?? null;

  const selectOptions = useMemo(
    () => [{ id: 0, name: "All categories" }, ...categories],
    [categories]
  );
  const normalizedSearchQuery = useMemo(
    () => searchQuery.trim().toLowerCase(),
    [searchQuery]
  );
  const filteredItems = useMemo(() => {
    if (!normalizedSearchQuery) return items;
    return items.filter((doc) => {
      const searchableText = [
        getExplicitDocumentDescription(doc),
        String(doc.filename ?? ""),
        String(getDocumentItemId(doc) ?? ""),
      ]
        .join(" ")
        .toLowerCase();
      return searchableText.includes(normalizedSearchQuery);
    });
  }, [items, normalizedSearchQuery]);

  useEffect(() => {
    if (!isOpen) return;
    if (categories.length === 0) {
      void listCategoriesAction(instanceId, documentScope);
    }
  }, [isOpen, categories.length, documentScope, instanceId, listCategoriesAction]);

  useEffect(() => {
    if (!isOpen) return;
    void listDocumentsAction(instanceId, categoryId, 0, documentScope);
  }, [isOpen, instanceId, listDocumentsAction, documentScope]);

  useEffect(() => {
    if (isOpen) return;
    setSearchQuery("");
    setPendingFile(null);
    setPendingDescription("");
    setPendingCategoryId(0);
  }, [isOpen]);

  function handlePick(item: DocumentValue) {
    onPick(toDocumentValue(item));
    onClose();
  }

  async function handleUpload() {
    if (!pendingFile) return;
    try {
      const doc = await uploadDocumentAction(
        instanceId,
        pendingFile,
        pendingCategoryId,
        pendingDescription.trim() || undefined
      );
      onPick(toDocumentValue(doc));
      // Refresh the list and sync category via the loader action.
      void listDocumentsAction(instanceId, pendingCategoryId, 0, documentScope);
      setPendingFile(null);
      setPendingDescription("");
      setPendingCategoryId(0);
    } catch {
      // Error already handled in store.
    }
  }

  function beginUpload(file: File, contextCategoryId: number) {
    const defaultDescription = file.name.replace(/\.[^/.]+$/, "");
    setPendingFile(file);
    setPendingCategoryId(contextCategoryId);
    setPendingDescription(defaultDescription);
  }

  function clearPendingUpload() {
    setPendingFile(null);
    setPendingDescription("");
    setPendingCategoryId(0);
  }

  function formatFileSize(size: number): string {
    if (!Number.isFinite(size)) return "Unknown";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let value = size;
    let idx = 0;
    while (value >= 1024 && idx < units.length - 1) {
      value /= 1024;
      idx += 1;
    }
    return `${value.toFixed(value < 10 && idx > 0 ? 1 : 0)} ${units[idx]}`;
  }

  const pendingFileType = useMemo(() => {
    if (!pendingFile) return "Unknown";
    const ext = pendingFile.name.split(".").pop() || "";
    return ext ? ext.toUpperCase() : "Unknown";
  }, [pendingFile]);

  if (!isOpen) return null;

  const scopeOptions = [
    { id: "current", label: "My Documents" },
    { id: "shared", label: "Shared Documents" },
  ] as const;
  const canUseSharedScope = sharedScopeAvailable !== false;
  const isSharedScope = documentScope === "shared";

  return (
    <>
      <div
        className="modal fade show"
        role="dialog"
        aria-modal="true"
        style={{ display: "block" }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex flex-column w-100 gap-2">
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <h5 className="modal-title mb-0">
                    {pendingFile ? "Confirm upload" : "Select a document"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close me-0"
                    aria-label="Close"
                    style={{ boxSizing: "content-box" }}
                    onClick={onClose}
                  />
                </div>

                {!pendingFile ? (
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
                      <div className="d-flex align-items-center gap-2 w-100">
                        <label
                          className="small text-body-secondary mb-0"
                          htmlFor={`doc-scope-${instanceId}`}
                        >
                          Document Set
                        </label>
                        <select
                          id={`doc-scope-${instanceId}`}
                          className="form-select form-select-sm w-100 w-md-auto"
                          value={documentScope}
                          onChange={(e) => {
                            const nextScope = e.target.value === "shared" ? "shared" : "current";
                            setDocumentsBrowserScope(instanceId, nextScope);
                            void listCategoriesAction(instanceId, nextScope);
                            void listDocumentsAction(instanceId, 0, 0, nextScope);
                          }}
                        >
                          {scopeOptions
                            .filter((opt) => opt.id !== "shared" || canUseSharedScope)
                            .map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.label}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="d-flex align-items-center gap-2 w-100">
                        <label
                          className="small text-body-secondary mb-0"
                          htmlFor={`doc-category-${instanceId}`}
                        >
                          Category
                        </label>
                        <select
                          id={`doc-category-${instanceId}`}
                          className="form-select form-select-sm w-100 w-md-auto"
                          value={categoryId}
                          onChange={(e) => {
                            const nextCategoryId = Number(e.target.value);
                            void listDocumentsAction(instanceId, nextCategoryId, 0, documentScope);
                          }}
                          disabled={isLoadingCategories}
                        >
                          {selectOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <input
                      type="search"
                      className="form-control form-control-sm"
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                ) : null}
              </div>
            </div>

            <div className="modal-body">
              {pendingFile ? (
                <div>
                  <div className="mb-3">
                    <div className="fw-semibold mb-1">File</div>
                    <div className="small text-body-secondary">
                      {pendingFile.name} · {formatFileSize(pendingFile.size)} ·{" "}
                      {pendingFileType}
                    </div>
                    <label className="btn btn-sm btn-outline-primary mt-2 mb-0">
                      Change file
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                        style={{ display: "none" }}
                        disabled={isUploading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.currentTarget.value = "";
                          if (file) beginUpload(file, pendingCategoryId);
                        }}
                      />
                    </label>
                  </div>
                  <div className="mb-3">
                    <label
                      htmlFor={`confirm-category-${instanceId}`}
                      className="form-label"
                    >
                      Category
                    </label>
                    <select
                      id={`confirm-category-${instanceId}`}
                      className="form-select form-select-sm"
                      value={pendingCategoryId}
                      onChange={(e) => setPendingCategoryId(Number(e.target.value))}
                    >
                      {selectOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label
                      htmlFor={`confirm-description-${instanceId}`}
                      className="form-label"
                    >
                      Description
                    </label>
                    <input
                      id={`confirm-description-${instanceId}`}
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Document description"
                      value={pendingDescription}
                      onChange={(e) => setPendingDescription(e.target.value)}
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={clearPendingUpload}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleUpload}
                      disabled={isUploading}
                    >
                      {isUploading ? "Uploading..." : "Upload"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {isLoadingCategories ? (
                    <div className="d-flex align-items-center gap-2 small text-body-secondary mb-2">
                      <span
                        className="spinner-border spinner-border-sm"
                        aria-hidden="true"
                      />
                      Loading categories...
                    </div>
                  ) : null}
                  {!isLoadingCategories && categoriesError ? (
                    <div
                      className="alert alert-warning py-2 px-3 small mb-2"
                      role="alert"
                    >
                      <div className="fw-semibold">
                        We could not load categories.
                      </div>
                      <div className="text-body-secondary">
                        Please try again in a moment.
                      </div>
                    </div>
                  ) : null}

                  {isLoading ? (
                    <div className="d-flex align-items-center gap-2 small text-body-secondary">
                      <span
                        className="spinner-border spinner-border-sm"
                        aria-hidden="true"
                      />
                      Loading...
                    </div>
                  ) : null}
                  {!isLoading && listError ? (
                    <div
                      className="alert alert-warning py-2 px-3 small mb-2"
                      role="alert"
                    >
                      <div className="fw-semibold">
                        We could not load documents.
                      </div>
                      <div className="text-body-secondary">
                        Please try again in a moment.
                      </div>
                    </div>
                  ) : null}

                  <div
                    className="d-flex align-items-center justify-content-between mb-2"
                    style={{ minHeight: "32px" }}
                  >
                    <div className="small fw-semibold">
                      {isSharedScope ? "Shared Documents" : "Documents"}
                    </div>
                    {!isSharedScope ? (
                      <label className="btn btn-xs btn-primary mb-0 text-nowrap">
                        Upload New Document
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                          style={{ display: "none" }}
                          disabled={isUploading}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.currentTarget.value = "";
                            if (file) beginUpload(file, categoryId);
                          }}
                        />
                      </label>
                    ) : null}
                  </div>

                  <div className="list-group">
                    {filteredItems.length === 0 ? (
                      <div className="small text-body-secondary">
                        {items.length === 0
                          ? "No documents"
                          : "No documents match your search"}
                      </div>
                    ) : (
                      filteredItems.map((doc, index) => {
                        const documentDescription =
                          getExplicitDocumentDescription(doc) || "No description available";
                        const documentId = getDocumentItemId(doc);
                        const headingTitle = documentId
                          ? `${documentDescription} (#${documentId})`
                          : documentDescription;
                        const itemKey = String(
                          documentId ?? doc.id ?? doc.filename ?? doc.url ?? index
                        );

                        return (
                        <button
                          key={itemKey}
                          type="button"
                          className="list-group-item list-group-item-action d-flex flex-column gap-1"
                          onClick={() =>
                            handlePick({
                              id: doc.id,
                              item_id: documentId,
                              url: doc.url,
                              item_name: getExplicitDocumentDescription(doc) || undefined,
                              description: getExplicitDocumentDescription(doc) || undefined,
                              name: doc.name,
                              filename: doc.filename,
                              categoryName: doc.categoryName,
                              groupName: doc.groupName,
                              publishDate: doc.publishDate,
                              year: doc.year,
                            })
                          }
                        >
                          <div className="d-flex align-items-start justify-content-between gap-2">
                            <div className="fw-semibold text-truncate" title={headingTitle}>
                              {documentDescription}
                              {documentId ? ` (#${documentId})` : ""}
                            </div>
                            {doc.publishDate ? (
                              <span className="text-gray-600 fs-14 fst-italic flex-shrink-0">
                                {doc.publishDate}
                              </span>
                            ) : null}
                          </div>
                          <div
                            className="small text-body-secondary text-truncate"
                            title={doc.filename}
                          >
                            {doc.filename}
                          </div>
                          <div className="d-flex flex-wrap gap-1">
                            {categoryId === 0 && doc.categoryName ? (
                              <span className="badge border border-primary text-primary">
                                {doc.categoryName}
                              </span>
                            ) : null}
                          </div>
                        </button>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show" onMouseDown={onClose} />
    </>
  );
}
